import {
  Truck, Boxes, UserCog, Users, Wallet, Wrench, Shield, Car, Fuel,
  LayoutGrid, Package, FolderKanban, Briefcase, Building2, ClipboardList, BarChart3, Scale,
} from "lucide-react";

// Icons selectable for custom groups (name -> component)
export const GROUP_ICONS = {
  Boxes, Truck, UserCog, Users, Wallet, Wrench, Shield, Car, Fuel,
  LayoutGrid, Package, FolderKanban, Briefcase, Building2, ClipboardList, BarChart3, Scale,
};
export const GROUP_ICON_NAMES = Object.keys(GROUP_ICONS);
export const resolveGroupIcon = (name) => GROUP_ICONS[name] || FolderKanban;

// Master list of atomic sections. `key` maps to i18n dash.nav.<key>; `label` is a fixed fallback.
// `special` sections render as their own nested dropdown (they have sub-views).
export const LEAF_META = {
  overview: { key: "overview" },
  employees: { key: "employees" },
  branches: { key: "branches" },
  analytics: { key: "analytics" },
  custody: { key: "custody" },
  vehicles: { key: "vehicles" },
  violations: { key: "violations" },
  accidents: { key: "accidents" },
  fuel: { key: "fuel" },
  company: { key: "company" },
  "var-expenses": { label: "المصروفات المتغيرة" },
  pnl: { label: "قائمة الأرباح والخسائر" },
  insurance: { key: "insurance", special: true },
  riders: { key: "riders", special: true },
  wallets: { key: "wallets" },
  "rider-cash": { key: "riderCash" },
  "salary-sim": { key: "salarySim" },
  "portal-usage": { key: "portalUsage" },
  revenues: { key: "revenues" },
  "reports-hub": { key: "reportsHub" },
  maintenance: { key: "maintenance", special: true },
  salaries: { key: "salaries" },
  leave: { key: "leave" },
  injuries: { key: "injuries" },
  loyalty: { key: "loyalty" },
  purchases: { key: "purchases" },
  "perf-alerts": { key: "perfAlerts" },
  meetings: { label: "الاجتماعات" },
  "biz-calendar": { label: "التقويم" },
  workspace: { label: "مساحة العمل" },
  sales: { key: "sales" },
  attendance: { key: "attendance" },
  advances: { key: "advances" },
  tasks: { key: "tasks" },
  alerts: { key: "alerts" },
  correspondence: { label: "المراسلات الإدارية" },
  "wa-messages": { label: "رسائل واتساب" },
  outlook: { label: "بريد Outlook" },
  "private-docs": { label: "وثائق خاصة" },
  contracts: { label: "إدارة العقود" },
  decisions: { label: "القرارات الإدارية" },
  library: { label: "مكتبة الشركة" },
  "saudi-files": { label: "ملفات الموظفين السعوديين" },
  "rider-cards": { label: "بطاقة المندوب" },
  "rider-map": { label: "خريطة المناديب" },
  "live-roster": { label: "المراقبة الحية" },
  lockers: { label: "اللوكر" },
  rentals: { label: "التأجير" },
  "faq-contacts": { label: "الأسئلة والأرقام" },
  "legal-cases": { label: "الحالات القانونية" },
  "legal-new": { label: "فتح حالة جديدة" },
  "legal-archive": { label: "أرشيف القضايا" },
};

export const ALL_LEAF_VALUES = Object.keys(LEAF_META).filter((v) => !["rider-map", "lockers"].includes(v));

// Sub-views for special (multi-view) sections: [viewValue, i18nKey]
export const SPECIAL_SUBVIEWS = {
  riders: [["riders", "ridersGeneral"], ["riders-daily", "ridersDaily"], ["riders-compare", "ridersCompare"], ["riders-target", "ridersTarget"], ["riders-distances", "ridersDistances"], ["riders-rented", "ridersRented"]],
  maintenance: [["car-maintenance", "carMaintenance"], ["moto-maintenance", "motoMaintenance"], ["spareparts", "spareparts"]],
  insurance: [["insurance-individuals", "insuranceIndividuals"], ["insurance-cars", "insuranceCars"], ["insurance-moto", "insuranceMoto"]],
};

// Default groups keep an i18n key equal to their id.
export const DEFAULT_GROUP_IDS = ["hr", "assets", "fleet", "admin", "finance"];
const FINANCE_GROUP = { t: "group", id: "finance", icon: "Wallet", children: ["revenues", "salaries", "salary-sim", "advances", "rider-cash", "company", "var-expenses", "pnl"] };
const BUSINESS_GROUP = { t: "group", id: "business", icon: "Briefcase", label: "إدارة الأعمال", children: ["workspace", "meetings", "biz-calendar"] };
const LEGAL_GROUP = { t: "group", id: "legal", icon: "Scale", label: "الشؤون القانونية", children: ["legal-cases", "legal-new", "legal-archive"] };
const ADMIN_GROUP = { t: "group", id: "admin", icon: "Briefcase", children: ["leave", "tasks", "correspondence", "wa-messages", "outlook", "decisions", "library", "saudi-files", "private-docs", "contracts"] };

// The default navigation layout (top-level ordered nodes).
export const DEFAULT_LAYOUT = [
  { t: "item", v: "overview" },
  { t: "group", id: "hr", icon: "UserCog", children: ["employees", "rider-cards", "rider-map", "live-roster", "injuries", "attendance", "loyalty", "perf-alerts"] },
  BUSINESS_GROUP,
  FINANCE_GROUP,
  ADMIN_GROUP,
  LEGAL_GROUP,
  { t: "item", v: "branches" },
  { t: "group", id: "assets", icon: "Boxes", children: ["custody", "wallets", "purchases"] },
  { t: "group", id: "fleet", icon: "Truck", children: ["vehicles", "rentals", "violations", "accidents", "fuel"] },
  { t: "item", v: "insurance" },
  { t: "item", v: "riders" },
  { t: "item", v: "portal-usage" },
  { t: "item", v: "reports-hub" },
  { t: "item", v: "maintenance" },
  { t: "item", v: "sales" },
  { t: "item", v: "alerts" },
  { t: "item", v: "lockers" },
  { t: "item", v: "faq-contacts" },
];

export const leafLabel = (value, labels, t) => {
  const custom = labels && labels[value];
  if (custom && String(custom).trim()) return String(custom).trim();
  const meta = LEAF_META[value] || {};
  if (meta.key) return t(`dash.nav.${meta.key}`);
  return meta.label || value;
};

export const groupLabel = (node, t) => {
  if (node.label && String(node.label).trim()) return String(node.label).trim();
  if (DEFAULT_GROUP_IDS.includes(node.id)) return t(`dash.nav.${node.id}`);
  return node.id || "مجموعة";
};

// Coerce a stored layout into a safe, de-duplicated structure. Falls back to DEFAULT_LAYOUT.
// sections merged into another page (analytics now lives inside "المعلومات"/branches)
const MERGED = new Set(["analytics", "rider-map", "lockers"]);
export const normalizeLayout = (raw) => {
  if (!Array.isArray(raw) || !raw.length) return DEFAULT_LAYOUT.map((n) => ({ ...n }));
  // one-time migration: saved layouts without the "admin" group get it, pulling its sections in
  if (!raw.some((n) => n && n.t === "group" && n.id === "admin")) {
    const moved = new Set(ADMIN_GROUP.children);
    const stripped = raw.filter((n) => !(n && n.t === "item" && moved.has(n.v)))
      .map((n) => (n && n.t === "group" ? { ...n, children: (n.children || []).filter((v) => !moved.has(v)) } : n));
    const hrIdx = stripped.findIndex((n) => n && n.t === "group" && n.id === "hr");
    stripped.splice(hrIdx >= 0 ? hrIdx + 1 : 1, 0, { ...ADMIN_GROUP });
    raw = stripped;
  }
  // migration: add "rider-cards" under HR for saved layouts
  if (!raw.some((n) => n && ((n.t === "item" && n.v === "rider-cards") || (n.t === "group" && (n.children || []).includes("rider-cards"))))) {
    raw = raw.map((n) => (n && n.t === "group" && n.id === "hr" ? { ...n, children: [...(n.children || []).slice(0, 1), "rider-cards", ...(n.children || []).slice(1)] } : n));
  }
  if (!raw.some((n) => n && ((n.t === "item" && n.v === "rider-map") || (n.t === "group" && (n.children || []).includes("rider-map"))))) {
    raw = raw.map((n) => { if (!(n && n.t === "group" && n.id === "hr")) return n; const c = [...(n.children || [])]; const i = c.indexOf("rider-cards"); c.splice(i >= 0 ? i + 1 : 1, 0, "rider-map"); return { ...n, children: c }; });
  }
  if (!raw.some((n) => n && ((n.t === "item" && n.v === "live-roster") || (n.t === "group" && (n.children || []).includes("live-roster"))))) {
    raw = raw.map((n) => { if (!(n && n.t === "group" && n.id === "hr")) return n; const c = [...(n.children || [])]; const i = c.indexOf("rider-map"); c.splice(i >= 0 ? i + 1 : 1, 0, "live-roster"); return { ...n, children: c }; });
  }
  // migration: add "outlook" after correspondence in the admin group
  if (!raw.some((n) => n && ((n.t === "item" && n.v === "outlook") || (n.t === "group" && (n.children || []).includes("outlook"))))) {
    raw = raw.map((n) => { if (!(n && n.t === "group" && n.id === "admin")) return n; const c = [...(n.children || [])]; const i = c.indexOf("correspondence"); c.splice(i >= 0 ? i + 1 : c.length, 0, "outlook"); return { ...n, children: c }; });
  }
  if (!raw.some((n) => n && ((n.t === "item" && n.v === "private-docs") || (n.t === "group" && (n.children || []).includes("private-docs"))))) {
    raw = raw.map((n) => (n && n.t === "group" && n.id === "admin" ? { ...n, children: [...(n.children || []), "private-docs"] } : n));
  }
  if (!raw.some((n) => n && ((n.t === "item" && n.v === "accidents") || (n.t === "group" && (n.children || []).includes("accidents"))))) {
    raw = raw.map((n) => { if (!(n && n.t === "group" && n.id === "fleet")) return n; const c = [...(n.children || [])]; const i = c.indexOf("violations"); c.splice(i >= 0 ? i + 1 : c.length, 0, "accidents"); return { ...n, children: c }; });
  }
  if (!raw.some((n) => n && ((n.t === "item" && n.v === "rentals") || (n.t === "group" && (n.children || []).includes("rentals"))))) {
    raw = raw.map((n) => { if (!(n && n.t === "group" && n.id === "fleet")) return n; const c = [...(n.children || [])]; const i = c.indexOf("vehicles"); c.splice(i >= 0 ? i + 1 : c.length, 0, "rentals"); return { ...n, children: c }; });
  }
  if (!raw.some((n) => n && ((n.t === "item" && n.v === "contracts") || (n.t === "group" && (n.children || []).includes("contracts"))))) {
    raw = raw.map((n) => (n && n.t === "group" && n.id === "admin" ? { ...n, children: [...(n.children || []), "contracts"] } : n));
  }
  // migration: create the "finance" group and move its sections into it
  if (!raw.some((n) => n && n.t === "group" && n.id === "finance")) {
    const moved = new Set(FINANCE_GROUP.children);
    const stripped = raw.filter((n) => !(n && n.t === "item" && moved.has(n.v)))
      .map((n) => (n && n.t === "group" ? { ...n, children: (n.children || []).filter((v) => !moved.has(v)) } : n));
    const hrIdx = stripped.findIndex((n) => n && n.t === "group" && n.id === "hr");
    stripped.splice(hrIdx >= 0 ? hrIdx + 1 : 1, 0, { ...FINANCE_GROUP });
    raw = stripped;
  }
  if (!raw.some((n) => n && ((n.t === "item" && n.v === "pnl") || (n.t === "group" && (n.children || []).includes("pnl"))))) {
    raw = raw.map((n) => (n && n.t === "group" && n.id === "finance" ? { ...n, children: [...(n.children || []), "pnl"] } : n));
  }
  if (!raw.some((n) => n && ((n.t === "item" && n.v === "workspace") || (n.t === "group" && (n.children || []).includes("workspace"))))) {
    raw = raw.map((n) => (n && n.t === "group" && n.id === "business" ? { ...n, children: ["workspace", ...(n.children || [])] } : n));
  }
  if (!raw.some((n) => n && n.t === "group" && n.id === "business")) {
    const moved = new Set(BUSINESS_GROUP.children);
    raw = raw.filter((n) => !(n && n.t === "item" && moved.has(n.v)));
    const i = raw.findIndex((n) => n && n.t === "group" && n.id === "hr");
    raw.splice(i >= 0 ? i + 1 : raw.length, 0, { ...BUSINESS_GROUP });
  }
  if (!raw.some((n) => n && n.t === "group" && n.id === "legal")) {
    const moved = new Set(LEGAL_GROUP.children);
    raw = raw.filter((n) => !(n && n.t === "item" && moved.has(n.v)));
    const i = raw.findIndex((n) => n && n.t === "group" && n.id === "admin");
    raw.splice(i >= 0 ? i + 1 : raw.length, 0, { ...LEGAL_GROUP });
  }
  const seen = new Set();
  const out = [];
  for (const node of raw) {
    if (!node || typeof node !== "object") continue;
    if (node.t === "group" && node.id) {
      const children = (Array.isArray(node.children) ? node.children : [])
        .filter((v) => LEAF_META[v] && !seen.has(v) && !MERGED.has(v));
      children.forEach((v) => seen.add(v));
      out.push({ t: "group", id: node.id, icon: node.icon || "FolderKanban", label: node.label || "", children });
    } else if (node.t === "item" && LEAF_META[node.v] && !seen.has(node.v) && !MERGED.has(node.v)) {
      seen.add(node.v);
      out.push({ t: "item", v: node.v });
    }
  }
  // append any leaves not referenced yet so nothing disappears
  for (const v of ALL_LEAF_VALUES) {
    if (!seen.has(v) && !MERGED.has(v)) out.push({ t: "item", v });
  }
  return out;
};

// Build the render tree + search/breadcrumb helpers.
// available: (value) => bool ; locks: { value: bool }
export const buildNavModel = ({ layout, labels, available, locks, t }) => {
  const norm = normalizeLayout(layout);
  const lk = locks || {};
  const breadcrumbs = {};
  const selectable = [];

  const makeLeaf = (value, ancestorLabels) => {
    const label = leafLabel(value, labels, t);
    const meta = LEAF_META[value] || {};
    const locked = !!lk[value];
    if (meta.special) {
      const children = SPECIAL_SUBVIEWS[value].map(([sv, k]) => ({ value: sv, label: t(`dash.nav.${k}`) }));
      breadcrumbs[value] = [...ancestorLabels, label].join(" — ");
      selectable.push({ value, label: breadcrumbs[value], locked });
      children.forEach((c) => {
        breadcrumbs[c.value] = [...ancestorLabels, label, c.label].join(" — ");
        selectable.push({ value: c.value, label: breadcrumbs[c.value] });
      });
      return { kind: "special", value, label, locked, children };
    }
    breadcrumbs[value] = [...ancestorLabels, label].join(" — ");
    selectable.push({ value, label: breadcrumbs[value], locked });
    return { kind: "leaf", value, label, locked };
  };

  const tree = [];
  for (const node of norm) {
    if (node.t === "group") {
      const glabel = groupLabel(node, t);
      const kids = node.children
        .filter((v) => available(v))
        .map((v) => makeLeaf(v, [glabel]));
      if (!kids.length) continue;
      tree.push({ kind: "group", id: node.id, label: glabel, icon: resolveGroupIcon(node.icon), children: kids });
    } else if (node.t === "item") {
      if (!available(node.v)) continue;
      tree.push(makeLeaf(node.v, []));
    }
  }
  return { tree, selectable, breadcrumbs };
};
